import React, { useState } from "react";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";
import { Modal, Button, Field, ErrorMessage } from "./components.jsx";
import { PACKAGES, money, linePrice } from "../shared/catalog.js";
import { fullAddress } from "../shared/deliveries.js";
import { PaymentForm } from "./workshop-payment.jsx";

export function OrderDetails({
  order: initialOrder,
  admins,
  onUpdate,
  onClose,
  onBuild,
}) {
  const [order, setOrder] = useState(initialOrder),
    [notes, setNotes] = useState(initialOrder.notes),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState(false);
  async function update(patch) {
    const result = await onUpdate(order, patch);
    setOrder(result);
    return result;
  }
  return (
    <Modal
      title={`${order.number} · ${order.customer.name}`}
      onClose={onClose}
      wide
    >
      <div className="order-details">
        <div className="detail-status-row">
          <span className={`badge badge-${order.status}`}>
            {order.status === "ready" ? "Ready for delivery" : order.status}
          </span>
          <span
            className={`badge ${order.received >= order.total ? "badge-paid" : "badge-cash"}`}
          >
            {order.received >= order.total
              ? "Paid"
              : `${money(order.total - order.received)} due`}
          </span>
        </div>
        <div className="detail-contact">
          <a href={`mailto:${order.customer.email}`}>
            <Mail size={16} />
            {order.customer.email}
          </a>
          <a href={`tel:${order.customer.phone}`}>
            <Phone size={16} />
            {order.customer.phone}
          </a>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress(order))}`}
            target="_blank"
            rel="noreferrer"
          >
            <MapPin size={16} />
            {fullAddress(order)} ↗
          </a>
          {order.customer.instructions && (
            <p>
              <strong>Delivery instructions:</strong>{" "}
              {order.customer.instructions}
            </p>
          )}
        </div>
        <div className="detail-section">
          <h3>The family’s package</h3>
          {order.items.map((item, i) => (
            <div className="detail-package" key={i}>
              <div className="detail-package-heading">
                <strong>
                  {PACKAGES.find((p) => p.id === item.packageId).name}
                </strong>
                <span>{money(linePrice(item))}</span>
              </div>
              {item.children.map((c, j) => (
                <div className="detail-child" key={j}>
                  <div>
                    <strong>{c.name}</strong>
                    <span>Age {c.age}</span>
                    {c.note && <p>{c.note}</p>}
                  </div>
                  <small>
                    {c.letterReceived
                      ? "Letter received"
                      : "No letter received"}
                  </small>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="detail-section">
          <h3>Money received</h3>
          <PaymentForm order={order} onUpdate={update} />
        </div>
        <div className="detail-section">
          <Field label="Internal notes">
            <textarea
              maxLength={4000}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
          <Button
            className="button-outline button-small"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await update({
                  action: "details",
                  notes,
                  letters: order.items.map((i) =>
                    i.children.map((c) => c.letterReceived),
                  ),
                });
                setSaved(true);
              } catch (e) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save internal notes
          </Button>
        </div>
        {saved && (
          <p role="status" className="success-message">
            Notes saved.
          </p>
        )}
        <ErrorMessage>{error}</ErrorMessage>
        {["new", "preparing"].includes(order.status) && (
          <div className="detail-section">
            <p>
              {order.assignedTo
                ? `Claimed by ${admins.find((a) => a.id === order.assignedTo)?.name.split(" ")[0]}.`
                : "Waiting in the Build queue."}
            </p>
            <Button onClick={onBuild}>
              Go to guided Build <ArrowRight size={16} />
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
